/**
 * MCP servers a skill can use, keyed by the ids listed in a skill's
 * `mcp-servers` and `mcp-servers-optional` metadata. Logos live in
 * public/mcp-logos. Every logo is drawn in the same square box, scaled to fit,
 * so tall or wide logos match the square ones. A logo that doesn't read on the dark theme has a
 * `logoDark` version. A server without a logo, and an id without an entry
 * here, render as text.
 */
export const MCP_SERVERS: Record<
  string,
  { label: string; logo?: string; logoDark?: string }
> = {
  // The brand symbol, from the on-light and on-dark logo lockups.
  securithings: {
    label: "SecuriThings",
    logo: "/securithings-symbol.svg",
    logoDark: "/securithings-symbol-dark.svg",
  },
  atlassian: { label: "Atlassian", logo: "/mcp-logos/atlassian.svg" },
  slack: { label: "Slack", logo: "/mcp-logos/slack.svg" },
  gmail: { label: "Gmail", logo: "/mcp-logos/gmail.svg" },
  // Google Sheets and Google Drive 2020 logos, public domain, from Wikimedia
  // Commons.
  "google-sheets": {
    label: "Google Sheets",
    logo: "/mcp-logos/google-sheets.svg",
  },
  "google-drive": {
    label: "Google Drive",
    logo: "/mcp-logos/google-drive.svg",
  },
};

export const McpServerLogo = ({
  id,
  optional = false,
  size = 20,
}: {
  id: string;
  optional?: boolean;
  size?: number;
}) => {
  const server = MCP_SERVERS[id];
  const label = `${server?.label ?? id}${optional ? " (optional)" : ""}`;

  if (server?.logo && server.logoDark) {
    return (
      <>
        <img
          src={server.logo}
          alt={label}
          title={label}
          width={size}
          height={size}
          style={{ width: size, height: size }}
          className="inline-block shrink-0 object-contain dark:hidden"
        />
        <img
          src={server.logoDark}
          alt={label}
          title={label}
          width={size}
          height={size}
          style={{ width: size, height: size }}
          className="hidden shrink-0 object-contain dark:inline-block"
        />
      </>
    );
  }

  return server?.logo ? (
    <img
      src={server.logo}
      alt={label}
      title={label}
      width={size}
      height={size}
      style={{ width: size, height: size }}
      className="inline-block shrink-0 object-contain"
    />
  ) : (
    <span
      title={label}
      className="rounded border border-border px-1.5 text-xs text-muted-foreground whitespace-nowrap"
    >
      {server?.label ?? id}
    </span>
  );
};

export const McpServerList = ({
  servers,
  optional = [],
}: {
  servers: string[];
  optional?: string[];
}) => (
  <div className="flex flex-wrap items-center gap-2">
    {servers.map((id) => (
      <McpServerLogo key={id} id={id} />
    ))}
    {optional.map((id) => (
      <McpServerLogo key={id} id={id} optional />
    ))}
  </div>
);

/** Logo and name for each server, used on a skill's page. */
export const McpServerChips = ({
  servers,
  optional = [],
}: {
  servers: string[];
  optional?: string[];
}) => (
  <div className="not-prose flex flex-wrap gap-2">
    {[
      ...servers.map((id) => ({ id, optional: false })),
      ...optional.map((id) => ({ id, optional: true })),
    ].map(({ id, optional: isOptional }) => (
      <span
        key={id}
        className="inline-flex items-center gap-2 rounded-md border border-border px-2.5 py-1.5 text-sm"
      >
        {MCP_SERVERS[id]?.logo && (
          <McpServerLogo id={id} optional={isOptional} size={16} />
        )}
        {MCP_SERVERS[id]?.label ?? id}
        {isOptional && (
          <span className="text-xs text-muted-foreground">Optional</span>
        )}
      </span>
    ))}
  </div>
);
